<template>
  <modal
    v-if="placePreview"
    :show="show"
    :title="placePreview.name"
    @save="close"
    :saveText="$t('OK')"
    @close="close"
    :hideActionsCancel="true"
    :sticky-header="true"
  >
    <v-container>
      <h5 class="text-center text-h5 d-block mt-8 pb-0">{{ $t('Details') }}</h5>
      <v-list class="py-0 px-4 px-sm-8">
        <v-list-item two-line class="pl-0 my-2">
          <v-list-item-content>
            <v-list-item-title class="mb-2">{{$t('Name')}}</v-list-item-title>
            <v-list-item-subtitle>{{ placePreview.name }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <template v-if="placePreview.primary_type">
          <v-divider ></v-divider>
          <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
              <v-list-item-title class="mb-2">{{$t('Type')}}</v-list-item-title>
              <v-list-item-subtitle>
                <v-chip
                  outlined
                  small
                >
                  {{ placePreview.primary_type.name[$i18n.locale] || placePreview.primary_type.name['en'] }}
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>

        <template v-if="placePreview.types && placePreview.types.length">
          <v-divider></v-divider>
          <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
              <v-list-item-title class="mb-2">{{ $t('All types') }}</v-list-item-title>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="(type, index) in placePreview.types"
                  :key="type.id"
                  outlined
                  small
                  :class="{ 'ml-2': index > 0 }"
                >
                  {{ type.name[$i18n.locale] || type.name['en'] }}
                </v-chip>
              </div>
            </v-list-item-content>
          </v-list-item>
        </template>

        <template v-if="placePreview.rating">
          <v-divider></v-divider>
          <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
              <v-list-item-title class="mb-2">{{$t('Rating')}}</v-list-item-title>
              <v-list-item-subtitle>
                <div class="d-flex align-center">
                  <rating-stars :rating="Number(placePreview.rating)"></rating-stars>
                  <span class="ml-2">{{ placePreview.rating }} ({{ $tc('reviews_count', placePreview.userRatingCount) }})</span>
                </div>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>

        <v-divider></v-divider>
        <v-list-item two-line class="pl-0 my-2">
          <v-list-item-content>
            <v-list-item-title class="mb-2">{{$t('Form Location Title')}}</v-list-item-title>
            <v-list-item-subtitle v-if="placePreview.city || placePreview.country">
              <template v-if="placePreview.city && placePreview.city.name">
                {{ placePreview.city.name[$i18n.locale] || placePreview.city.name['en'] }},
              </template>
              <template v-if="placePreview.country && placePreview.country.name">
                {{ placePreview.country.name[$i18n.locale] || placePreview.country.name['en'] }}
              </template>
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="placePreview.location.formattedAddress" class="mt-1">
              {{ placePreview.location.formattedAddress }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <template v-if="placePreview.internationalPhoneNumber">
          <v-divider></v-divider>
          <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
                <v-list-item-title class="mb-2">{{$t('Phone number')}}</v-list-item-title>
              <v-list-item-subtitle>{{ placePreview.internationalPhoneNumber }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>

        <template v-if="placePreview.price_level_display_text">
          <v-divider></v-divider>
          <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
              <v-list-item-title class="mb-2">{{$t('Price')}}</v-list-item-title>
              <v-list-item-subtitle>{{ placePreview.price_level_display_text }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>

        <template v-if="placePreview.business_status_display_text">
          <v-divider></v-divider>
          <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
              <v-list-item-title class="mb-2">Status</v-list-item-title>
              <v-list-item-subtitle>{{ placePreview.business_status_display_text }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>

        <template v-if="placePreview.content">
          <template v-if="placePreview.content.description_short">
            <v-divider></v-divider>
            <v-list-item two-line class="pl-0 my-2">
              <v-list-item-content>
                <v-list-item-title class="mb-2">{{$t('Form Info Short Description')}}</v-list-item-title>
                <div v-html="placePreview.content.description_short[$i18n.locale]" class="text-body-2 grey--text text--darken-2"></div>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-if="placePreview.content.description">
            <v-divider></v-divider>
            <v-list-item two-line class="pl-0 my-2">
              <v-list-item-content>
                <v-list-item-title class="mb-2">{{$t('Form Info Description')}}</v-list-item-title>
                <div v-html="placePreview.content.description[$i18n.locale]" class="text-body-2 grey--text text--darken-2"></div>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-if="placePreview.links && placePreview.links.websiteUri">
            <v-divider></v-divider>
            <v-list-item two-line class="pl-0 my-2">
              <v-list-item-content>
                <v-list-item-title class="mb-2">{{$t('Website')}}</v-list-item-title>
                <v-list-item-subtitle>
                  <a :href="placePreview.links.websiteUri" target="_blank">{{ placePreview.links.websiteUri }}</a>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-if="groupedTags.length">
            <v-divider></v-divider>
            <v-list-item class="pl-0 my-2 align-start">
              <v-list-item-content>
                <v-list-item-title class="mb-3">{{ $t('Amenities') }}</v-list-item-title>
                <div
                  v-for="group in groupedTags"
                  :key="group.name"
                  class="mb-4"
                >
                  <div class="text-subtitle-2 mb-2">{{ group.name }}</div>
                  <div class="d-flex flex-wrap">
                    <v-chip
                      v-for="tag in group.tags"
                      :key="`${group.name}-${tag.name}`"
                      class="ma-1"
                      small
                      :color="tag.value ? 'green lighten-5' : 'red lighten-5'"
                    >
                      <v-icon left small>{{ tag.value ? 'mdi-check' : 'mdi-close' }}</v-icon>
                      {{ tag.name }}
                    </v-chip>
                  </div>
                </div>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-if="placePreview.content.key_features && Array.isArray(placePreview.content.key_features)">
            <v-divider></v-divider>
            <v-list-item two-line class="pl-0 my-2">
              <v-list-item-content>
                <v-list-item-title class="mb-2">{{$t('Key features')}}</v-list-item-title>
                <div class="flex">
                  <v-chip v-for="(feature, index) in placePreview.content.key_features" :key="index" class="ma-1" outlined>
                    {{ feature[$i18n.locale] }}
                  </v-chip>
                </div>
              </v-list-item-content>
            </v-list-item>
          </template>
        </template>
      </v-list>

      <template v-if="placePreview.opening_hours">
        <h5 class="text-center text-h5 d-block mt-8 pb-0">{{ $t('Opening Hours') }}</h5>
        <v-list class="py-0 px-4 px-sm-8 pb-8">
          <div v-for="(openingHour, index) in placePreview.opening_hours_by_day || []" :key="index">
            <v-list-item class="px-0">
              <v-list-item-content>
                <div class="d-flex justify-space-between">
                  <span class="text-capitalize">{{ openingHour.day_name }}</span>
                  <span>
                  <template v-if="!openingHour.is_closed">
                      {{ openingHour.open_time }} - {{ openingHour.close_time }}h
                    </template>
                    <template v-else>
                      {{ $t('Closed') }}
                    </template>
                  </span>
                </div>
              </v-list-item-content>
            </v-list-item>
            <v-divider v-if="index < 6" :key="'div-' + index"></v-divider>
          </div>
        </v-list>
      </template>

      <template v-if="placePreview.latitude && placePreview.longitude">
        <h5 class="text-center text-h5 d-block mt-8 pb-4">{{ $t('Form Location Title') }}</h5>
        <div class="px-4 px-sm-8">
           <GmapMap
            ref="mapRef"
            :center="{ lat:Number(placePreview.latitude), lng:Number(placePreview.longitude) }"
            :zoom="16"
            :options="{ gestureHandling: 'cooperative' }"
            style="width: 100%; height: 400px"
          >
          </GmapMap>
        </div>
      </template>

      <template v-if="previewPhotos.length">
        <h5 class="text-center text-h5 d-block mt-16 pb-4">{{ $t('Photos') }}</h5>
        <div class="px-4 px-sm-8">
          <v-carousel
            hide-delimiter-background
            height="400"
            class="rounded-lg"
            cycle
            hide-delimiters
          >
            <v-carousel-item
              v-for="(photo, index) in previewPhotos"
              :key="index"
            >
              <v-img
                src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=="
                :srcset="photo.srcset"
                sizes="(max-width: 700px) 100vw, 700px"
                height="100%"
                class="grey lighten-2"
              >
                <template v-slot:placeholder>
                  <v-row
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <v-progress-circular
                      indeterminate
                      color="grey lighten-5"
                    ></v-progress-circular>
                  </v-row>
                </template>
                <div v-if="photo.authorAttributionsDisplayName" class="white--text pa-4 pb-10 text-caption">
                  <a
                    :href="photo.authorAttributionsUri || photo.googleMapsUri || '#'"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="white--text text-decoration-none text-outline"
                  >
                    <v-icon small color="white">mdi-copyright</v-icon> {{ photo.authorAttributionsDisplayName }}
                  </a>
                </div>
              </v-img>
            </v-carousel-item>
          </v-carousel>
        </div>
      </template>

      <template v-if="placePreview.has_socials">
        <h5 class="text-center text-h5 d-block mt-16 pb-0">{{ $t('Socials') }}</h5>
        <v-list class="py-0 px-4 px-sm-8">
          <template v-if="placePreview.socials.facebook">
            <v-list-item two-line class="pl-0 my-2">
              <v-list-item-content>
                <v-list-item-title class="mb-2">Facebook</v-list-item-title>
                <v-list-item-subtitle>
                  <a :href="placePreview.socials.facebook" target="_blank">{{ placePreview.socials.facebook }}</a>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-if="placePreview.socials.instagram">
            <v-divider v-if="placePreview.socials.facebook"></v-divider>
            <v-list-item two-line class="pl-0 my-2">
              <v-list-item-content>
                <v-list-item-title class="mb-2">Instagram</v-list-item-title>
                <v-list-item-subtitle>
                  <a :href="placePreview.socials.instagram" target="_blank">{{ placePreview.socials.instagram }}</a>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-if="placePreview.socials.tiktok_handle">
            <v-divider v-if="placePreview.socials.facebook || placePreview.socials.instagram"></v-divider>
            <v-list-item two-line class="pl-0 my-2">
              <v-list-item-content>
                <v-list-item-title class="mb-2">TikTok</v-list-item-title>
                 <v-list-item-subtitle>
                  <a :href="'https://www.tiktok.com/@' + placePreview.socials.tiktok_handle" target="_blank">
                    https://www.tiktok.com/@{{ placePreview.socials.tiktok_handle }}
                  </a>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-if="placePreview.socials.youtube_video_id">
            <v-divider v-if="placePreview.socials.facebook || placePreview.socials.instagram || placePreview.socials.tiktok_handle"></v-divider>
            <v-list-item two-line class="pl-0 my-2">
              <v-list-item-content>
                <v-list-item-title class="mb-2">YouTube</v-list-item-title>
                <v-list-item-subtitle>
                  <a :href="'https://www.youtube.com/watch?v=' + placePreview.socials.youtube_video_id" target="_blank">
                    https://www.youtube.com/watch?v={{ placePreview.socials.youtube_video_id }}
                  </a>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-list>
      </template>

      <template v-if="placePreview.reviews && placePreview.reviews.length">
        <h5 class="text-center text-h5 d-block mt-16 pb-4">{{ $t('Reviews') }}</h5>
        <div v-if="placePreview.rating" class="d-flex justify-center align-center font-weight-medium mb-4 text-h4">
          <v-icon size="24" color="#DC2626">mdi-star</v-icon>
          <span class="mx-1">{{ placePreview.rating }}</span>
          <v-icon size="24" color="#DC2626">mdi-star</v-icon>
        </div>
        <div class="py-0 px-4 px-sm-8">
          <v-list-item-subtitle class="text-center">
            {{ placePreview.content.reviews_summary[$i18n.locale] }}
          </v-list-item-subtitle>
          <v-list class="py-0 mt-6">
            <div v-for="(review, index) in placePreview.reviews" :key="review.id || index">
              <v-list-item class="px-0 d-block">
                <div class="d-flex align-center mb-3">
                  <a
                    v-if="review.authorAttributionsUri && review.authorAttributionsPhotoUri"
                    :href="review.authorAttributionsUri"
                    target="_blank"
                    class="text-decoration-none"
                  >
                    <v-img
                      :src="review.authorAttributionsPhotoUri"
                      height="52"
                      max-width="52"
                      contain
                      class="mr-2"
                    ></v-img>
                  </a>
                  <v-img
                    v-else-if="review.authorAttributionsPhotoUri"
                    :src="review.authorAttributionsPhotoUri"
                    height="52"
                    max-width="52"
                    contain
                    class="mr-2"
                  ></v-img>
                  <v-list-item-avatar v-else color="grey lighten-3">
                    <v-icon>mdi-account</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content class="pa-0">
                    <div class="d-flex justify-space-between align-start">
                      <div>
                        <a
                          v-if="review.authorAttributionsUri"
                          :href="review.authorAttributionsUri"
                          target="_blank"
                          class="text-decoration-none black--text font-weight-medium"
                          rel="noopener noreferrer"
                        >
                          {{ review.authorAttributionsDisplayName }}
                        </a>
                        <rating-stars :rating="Number(review.rating)" class="mt-1"></rating-stars>
                      </div>
                      <span class="caption grey--text text--darken-1">{{ formatDate(review.publishTime) }}</span>
                    </div>
                  </v-list-item-content>
                </div>

                <p class="text-body-2 mt-2 mb-0 grey--text text--darken-3">
                  {{ review.text }}
                </p>

                <div class="d-flex align-center text-body-2 mt-2">
                  <a
                    v-if="review.googleMapsUri"
                    :href="review.googleMapsUri"
                    target="_blank"
                    class="grey--text text--darken-1 text-decoration-underline font-weight-medium"
                  >{{ $t('See original') }}</a>
                  <span v-if="review.googleMapsUri && review.flagContentUri" class="mx-2 grey--text text--lighten-1">•</span>
                  <a
                    v-if="review.flagContentUri"
                    :href="review.flagContentUri"
                    target="_blank"
                    class="grey--text text--darken-1 text-decoration-underline font-weight-medium"
                  >{{ $t('Report this review') }}</a>
                </div>
              </v-list-item>
              <v-divider
                v-if="index < placePreview.reviews.length - 1"
                class="mt-6 mb-4"
              ></v-divider>
            </div>
          </v-list>
        </div>
      </template>
    </v-container>
  </modal>
</template>

<script>
import { mapGetters } from 'vuex';
import Modal from '@/components/Modal.vue';
import RatingStars from '@/components/RatingStars.vue';
import dateFormatMixin from '@/lib/DateFormatMixin';

export default {
  components: {
    Modal,
    RatingStars,
  },
  mixins: [dateFormatMixin],
  data() {
    return {
      show: false,
    };
  },
  computed: {
    ...mapGetters(['placePreview']),
    previewPhotos() {
      return this.placePreview
        ? this.placePreview.gallery_for_frontend
        : [];
    },
    groupedTags() {
      if (!this.placePreview || !this.placePreview.tags_grouped) {
        return [];
      }

      return Object.keys(this.placePreview.tags_grouped).map((name) => ({
        name,
        tags: this.placePreview.tags_grouped[name] || [],
      }));
    },
  },
  watch: {
    placePreview(val) {
      this.show = Boolean(val);
    },
  },
  methods: {
    close() {
      this.show = false;
      this.$store.dispatch('clearPlacePreview');
    },
  },
};
</script>
